import { ApolloProvider } from '@apollo/client';
import { useAuthContext } from '@shared/contexts/useAuthContext';
import { useEffect, useMemo, useRef } from 'react';

import { createApolloClientResources } from '../utils/apollo';

type ApolloWrapperProps = {
  appName: string;
  httpLinkUri: string;
  webSocketLinkUri?: string;
  children: JSX.Element;
};
export const ApolloWrapper = ({
  appName,
  httpLinkUri,
  webSocketLinkUri,
  children,
}: ApolloWrapperProps) => {
  const { token } = useAuthContext();
  const pendingDisposals = useRef(
    new Map<() => Promise<void>, ReturnType<typeof setTimeout>>()
  );

  const { client, dispose } = useMemo(
    () =>
      createApolloClientResources({
        appName,
        httpLinkUri,
        token,
        webSocketLinkUri,
      }),
    [appName, httpLinkUri, token, webSocketLinkUri]
  );

  useEffect(() => {
    const pendingDisposal = pendingDisposals.current.get(dispose);
    if (pendingDisposal) {
      clearTimeout(pendingDisposal);
      pendingDisposals.current.delete(dispose);
    }

    return () => {
      // NOTE(mike): Strict Mode remounts effects while retaining memoized resources.
      const timeout = setTimeout(() => {
        pendingDisposals.current.delete(dispose);
        void dispose().catch((error) => {
          console.error('Failed to dispose Apollo client', error);
        });
      });
      pendingDisposals.current.set(dispose, timeout);
    };
  }, [dispose]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
